import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../../data/content";
import { KineticLines, Eyebrow, EASE } from "../motion";

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} id="hero" className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Suite atypique immersive haut de gamme : bain rond sous une grande verrière, velours prune et lumière dorée — exemple d'investissement immobilier différenciant"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#150E1C]/70 via-[#150E1C]/30 to-[#150E1C]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#150E1C]/85 via-[#150E1C]/25 to-transparent" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[1500px] flex-col justify-center px-6 pb-16 pt-28 sm:px-10 lg:px-16 lg:justify-end lg:pb-20 lg:pt-36"
      >
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: EASE }}>
          <Eyebrow data-testid="hero-eyebrow">Investissement immobilier atypique & immersif</Eyebrow>
        </motion.div>

        <h1 className="mt-6 max-w-6xl font-serif text-[10.5vw] leading-[1.06] text-cream sm:mt-8 sm:text-5xl md:text-6xl lg:text-7xl" data-testid="hero-title">
          <KineticLines
            delay={0.35}
            lines={[
              <span key="1">Investissez autrement.</span>,
              <span key="2">
                Créez l'effet <em className="italic text-neon-light">WHAOU.</em>
              </span>,
              <span key="3">Et faites de l'atypique un investissement rentable.</span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
        >
          Demoiselle Atypique vous accompagne dans la création d'investissements immobiliers différenciants, désirables
          et pensés pour la rentabilité.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button onClick={() => navigate("/rendez-vous")} data-testid="hero-cta-projet" className="btn-primary">
            Parler de mon projet <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={() => navigate("/", { state: { scrollTo: "methode" } })} data-testid="hero-cta-methode" className="btn-outline">
            Découvrir notre méthode
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
